import { Button } from "@/components/Buttons";
import CouponCodeForm from "@/components/Form/CouponCodeForm";
import { ErrorMessage } from "@/components/Form/Parts/index";
import PaymentIconImage from "@/components/Images/PaymentIconImage";
import Loading from "@/components/Loading";
import { rightViewTerm } from "@/components/RightViewTerm";
import {
  usePaymentInstrumentInfoQuery,
  usePurchaseEstimateQuery,
  usePurchaseEstimateWithCouponLazyQuery,
} from "@/hooks";
import { PAYMENT_METHOD_ID, PAYMENT_METHOD_NAMES } from "@/lib/constants";
import dayjs from "@/lib/day";
import formatPrice from "@/lib/formatPrice";
import formStyles from "@/styles/Form.module.scss";
import styles from "@/styles/Payment.module.scss";
import processStyles from "@/styles/Process.module.scss";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

export default function PurchaseFormStep2({
  onSubmit,
  product,
  //broadcastDiscount,
  paymentMethodId,
  paymentInstrumentId,
  error,
  onBack,
  setSubmitted,
}) {
  const router = useRouter();
  const [unappliedCouponError, setUnppliedCouponError] = useState();
  const [applyVoucherCode, setApplyVoucherCode] = useState();

  // 視聴期間
  const [rightLabel, rightText] = rightViewTerm(product?.right);
  // 決済日
  const purchaseDate = dayjs().format("YYYY/MM/DD");
  // 販売期間
  const salesStartAt = product?.sales_start_at
    ? dayjs(product.sales_start_at).format("YYYY/MM/DD")
    : null;
  const salesEndAt = product?.sales_end_at
    ? dayjs(product.sales_end_at).format("YYYY/MM/DD")
    : null;

  // react-hook-form
  const { register, handleSubmit, formState, getValues, setValue, watch } =
    useForm({ mode: "all" });
  const { isValid, isSubmitting } = formState;
  const watchCouponCode = watch("couponCode");

  // 支払い方法
  const { data, loading } = usePaymentInstrumentInfoQuery({
    variables: { id: paymentInstrumentId },
  });
  const paymentInstrument = data?.paymentInstrumentInfo || {};
  const paymentMethod = useMemo(() => {
    if (paymentInstrument) {
      return paymentInstrument.payment_method_id;
    }
  }, [paymentInstrument]);

  const paymentIconClassName = useMemo(() => {
    return paymentMethod === PAYMENT_METHOD_ID.CREDIT_CARD_3DS
      ? styles.paymentCreditCardImage
      : styles.paymentOtherImage;
  }, [paymentMethod]);

  const creditCardInfo = useMemo(() => {
    if (paymentInstrument) {
      return {
        type: paymentInstrument?.values?.card_type,
        number: `****-****-****-${paymentInstrument?.values?.card_number}`,
      };
    }
  }, [paymentInstrument]);

  // 購入可能か確認
  const { data: estimateData, loading: estimateLoading } =
    usePurchaseEstimateQuery({
      variables: {
        productId: Number(product.id),
        paymentMethodId,
      },
      onCompleted: ({ estimate }) => {
        // エラーの場合は前のページに戻す
        if (estimate?.error) {
          onBack(estimate.error);
        }
      },
    });
  const estimate = estimateData?.estimate;

  // 一度の呼び出しでoncompletedが2回呼び出しされる場合があるのでフラグでoncompletedの処理が一度しか呼び出しされないように制御する
  const completedFlag = useRef();
  // クーポン
  const [
    estimateWithCouponQuery,
    { data: estimateWithCouponData, loading: couponLoading },
  ] = usePurchaseEstimateWithCouponLazyQuery({
    variables: { productId: Number(product.id), paymentMethodId },
    onError: ({ error }) => {},
  });
  const estimateWithCoupon = estimateWithCouponData?.estimateWithCoupon;
  const applyVoucher = estimateWithCoupon?.product?.voucherCode?.voucher;
  // 割引価格
  const discountPrice = useMemo(() => {
    // サーバー側で放送契約割引とキャンペーンの割引額を比較しているので
    // バウチャーが適用されたということは放送契約割引より安いということになる
    if (applyVoucher) {
      return {
        label: "適用後金額",
        value: estimateWithCoupon?.product?.discountPrice,
      };
    }
    // if (broadcastDiscount?.result) {
    //   return {
    //     label: "割引適用後金額",
    //     value: product?.right?.discount_price || 0,
    //   };
    // }
    return null;
  }, [product, /*broadcastDiscount,*/ applyVoucher, estimateWithCoupon]);

  // 割引の名前
  const discountName = useMemo(() => {
    if (applyVoucher) {
      return { label: "適用されるクーポン", value: applyVoucher.name };
    }
    // if (broadcastDiscount?.result) {
    //   return { label: "適用される割引", value: "放送契約者割引" };
    // }
  }, [applyVoucher]);

  // submitのコールバック
  const beforeSubmit = useCallback(
    (data) => {
      if (data.couponCode) {
        setUnppliedCouponError(
          "クーポンコード入力されていますが適用されていません。\n適用ボタンを押すか、入力をクリアしてから購入するボタンを押してください。"
        );
      } else {
        onSubmit({ ...data, ...{ applyVoucherCode } });
      }
    },
    [onSubmit, applyVoucherCode]
  );

  // 前のページに戻る
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  // クーポン適用
  const applyCoupon = useCallback(() => {
    const couponCode = getValues("couponCode");
    estimateWithCouponQuery({
      variables: {
        productId: Number(product.id),
        paymentMethodId: paymentMethod,
        voucherCode: couponCode,
        // userVoucherId: broadcastDiscount?.user_voucher_id,
      },
    });
    completedFlag.current = false;
  }, [
    paymentMethod,
    /*broadcastDiscount*/
  ]);

  // クーポンの適用キャンセル
  const handleCouponCancel = useCallback(() => {
    estimateWithCouponQuery({
      variables: {
        productId: Number(product.id),
        paymentMethodId: paymentMethod,
        couponCode: null,
      },
    });
    completedFlag.current = false;
  }, [paymentMethod]);

  // クーポンコードのフォームが空になったら購入ボタン上のエラーメッセージをリセット
  useEffect(() => {
    if (unappliedCouponError && !watchCouponCode) {
      setUnppliedCouponError("");
    }
  }, [unappliedCouponError, watchCouponCode]);

  // ページ遷移したら購入ボタンを有効にする
  useEffect(() => {
    const resetSubmitted = () => {
      setSubmitted(false);
    };
    router.events.on("routeChangeComplete", resetSubmitted);
    return () => {
      router.events.off("routeChangeComplete", resetSubmitted);
    };
  }, []);

  // クーポンが適応後にクーポン入力を非表示にする
  useEffect(() => {
    if (estimateWithCouponData && !completedFlag.current) {
      const voucherCode = estimateWithCouponData.estimateWithCoupon?.error
        ? null
        : getValues("couponCode");

      setApplyVoucherCode(voucherCode);
      setValue("couponCode", "", { shouldValidate: true });
      completedFlag.current = true;
    }
  }, [estimateWithCouponData, getValues]);

  if (loading || estimateLoading) return null;
  if (!estimate?.product) return null;

  // 放送契約割引がある
  // 視聴期間も表示する

  return (
    <>
      {couponLoading && <Loading fixed />}
      <div className={processStyles.contentWrapper}>
        <div className={`${processStyles.content} ${styles.container}`}>
          <p className={processStyles.contentHeader}>
            下記の内容で購入しますか？
          </p>
          <div className={styles.container}>
            <div className={`${styles.section} ${styles.column}`}>
              <div className={styles.table}>
                <div className={styles.row}>
                  <div className={styles.label}>商品名</div>
                  <div className={styles.value}>{product.name}</div>
                </div>
                <div className={styles.row}>
                  <div className={styles.label}>金額</div>
                  <div className={styles.value}>
                    <span className={styles.priceAmount}>
                      {formatPrice(product.price)}円<span>（税込）</span>
                    </span>
                  </div>
                </div>
                {!(discountPrice == null) && (
                  <>
                    <div className={styles.row}>
                      <div className={styles.label}>{discountName.label}</div>
                      <div className={styles.value}>
                        {discountName.value}
                        {applyVoucher && (
                          <a
                            className={`${styles.couponCancelLink} ${styles.inline}`}
                            onClick={handleCouponCancel}
                          >
                            適用を解除する
                          </a>
                        )}
                      </div>
                    </div>
                    <div className={styles.row}>
                      <div className={styles.label}>{discountPrice.label}</div>
                      <div className={styles.value}>
                        <span className={styles.priceAmount}>
                          {formatPrice(discountPrice.value)}円
                          <span>（税込）</span>
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className={styles.row}>
                  <div className={styles.label}>支払方法</div>
                  <div className={styles.value}>
                    <div>{PAYMENT_METHOD_NAMES[paymentMethod]}</div>
                    {(paymentMethod == PAYMENT_METHOD_ID.CREDIT_CARD_3DS ||
                      paymentMethod == PAYMENT_METHOD_ID.CREDIT_CARD_NO3DS) && (
                      <div>
                        <PaymentIconImage
                          paymentMethodId={paymentMethod}
                          cardType={creditCardInfo.type}
                          className={paymentIconClassName}
                        />
                        <span>{creditCardInfo.number}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.label}>決済日</div>
                  <div className={styles.value}>{purchaseDate}</div>
                </div>
                <div className={styles.row}>
                  <div className={styles.label}>販売期間</div>
                  <div className={styles.value}>
                    {salesStartAt} 〜 {salesEndAt}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.label}>{rightLabel}</div>
                  <div className={styles.value}>{rightText}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {error && (
        <div className={styles.creditCardError}>
          <ErrorMessage msg={error} />
        </div>
      )}
      <CouponCodeForm
        enable={!applyVoucherCode}
        register={register}
        apply={applyCoupon}
        error={estimateWithCoupon?.error}
      />
      <div className={formStyles.guideArea}>
        <p>
          解約について
          <br />
          商品の性質上、決済完了後のキャンセルや返金等には応じられません。
        </p>
      </div>
      <form onSubmit={handleSubmit(beforeSubmit)} noValidate>
        <div className={formStyles.btnArea}>
          <Button
            type="submit"
            fillColor
            fontFixed
            large
            disabled={!isValid}
            loading={isSubmitting}
          >
            購入する
          </Button>
          <div className={styles.backButton}>
            <a onClick={handleBack}>前の画面へ戻る</a>
          </div>
        </div>
      </form>
    </>
  );
}
